import { format } from "date-fns"
import { DATE_TIME_FORMAT, DAY_FORMAT } from "./constants"

export function formatCampaignDate(date: string | Date, displayHour: boolean = false) {
  if (!date) return ""
  if (typeof date === "string") {
    date = new Date(date)
  }

  if (displayHour) {
    return format(new Date(date), DATE_TIME_FORMAT)
  } else {
    return format(new Date(date), DAY_FORMAT)
  }
}

export function formatDate(date: string | Date, formatText: string) {
  if (typeof date === "string") {
    date = new Date(date)
  }

  return format(new Date(date), formatText || DATE_TIME_FORMAT)
}

// KA-1916: `audit_logs.created_at` is a Postgres `timestamp` (no time zone) holding UTC
// digits, so Hasura returns it bare ("2026-07-16T05:51:17.82") and `new Date()` would read
// it as the viewer's local time. These helpers parse such values as UTC and render them at
// a fixed offset instead — ops reads the change history in GMT+7.
export const DISPLAY_UTC_OFFSET_HOURS = 7

const HAS_TIME_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i

/** Parses a timestamp, treating a value with no time-zone designator as UTC. */
export function parseAsUtc(date: string | Date): Date {
  if (date instanceof Date) return date
  const trimmed = (date || "").trim()
  const withZone = HAS_TIME_ZONE.test(trimmed) ? trimmed : `${trimmed.replace(" ", "T")}Z`
  return new Date(withZone)
}

/**
 * Formats an instant at a fixed UTC offset (default GMT+7), independent of the browser's
 * time zone. `format` reads a Date through the local zone, so the instant is shifted such
 * that its local field getters read back the wall clock at `offsetHours`.
 */
export function formatDateAtOffset(
  date: string | Date,
  formatText: string,
  offsetHours: number = DISPLAY_UTC_OFFSET_HOURS,
) {
  const atOffset = new Date(parseAsUtc(date).getTime() + offsetHours * 3600 * 1000)
  const shifted = new Date(atOffset.getTime() + atOffset.getTimezoneOffset() * 60 * 1000)

  return format(shifted, formatText || DATE_TIME_FORMAT)
}

/** Formats an instant in UTC (used by exports that are specified as UTC). */
export function formatDateUtc(date: string | Date, formatText: string) {
  return formatDateAtOffset(date, formatText, 0)
}

export function formatCampaignPeriod(start: string | Date, end: string | Date) {
  if (start && end) {
    return `${formatCampaignDate(start)} - ${formatCampaignDate(end)}`
  }
  return ""
}
